import { Category, OfferType, RankType } from '@smg-real-estate/hg-rets-schema';
import { type LocationQuery } from 'vue-router';

import { InsertionStatus } from '@/types';

export type SearchFilterTypes =
  | string[]
  | InsertionStatus[]
  | OfferType[]
  | Category[]
  | RankType[]
  | InsertionSource[]
  | CompletenessLevel[]
  | PerformanceLevel[]
  | number
  | undefined;

export interface SearchFilterData {
  [key: string]: SearchFilterTypes;
  searchQuery?: string[];
  status?: InsertionStatus[];
  offerType?: OfferType[];
  category?: Category[];
  rankType?: RankType[];
  source?: InsertionSource[];
  completeness?: CompletenessLevel[];
  performance?: PerformanceLevel[];
  pageSize?: number;
}

export enum InsertionSource {
  MANUAL = 'MANUAL',
  CRM = 'CRM',
}

export enum CompletenessLevel {
  VERY_HIGH = 'VERY_HIGH',
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}

export enum PerformanceLevel {
  VERY_HIGH = 'VERY_HIGH',
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}

export type SearchControlTypes = number | SearchControlSortBy | string | undefined;

export enum SearchControlSortBy {
  LAST_UPDATE_ASC = 'lastUpdate-asc',
  LAST_UPDATE_DESC = 'lastUpdate-desc',
  CREATED_AT_ASC = 'createdAt-asc',
  CREATED_AT_DESC = 'createdAt-desc',
  COMPLETENESS_SCORE_ASC = 'completenessScore-asc',
  COMPLETENESS_SCORE_DESC = 'completenessScore-desc',
  PERFORMANCE_ASC = 'performance-asc',
  PERFORMANCE_DESC = 'performance-desc',
}

export const mappedTrackingSorting: Record<SearchControlSortBy, string> = {
  [SearchControlSortBy.LAST_UPDATE_DESC]: 'activation_date_desc',
  [SearchControlSortBy.LAST_UPDATE_ASC]: 'activation_date_asc',
  [SearchControlSortBy.CREATED_AT_DESC]: 'activation_date_desc',
  [SearchControlSortBy.CREATED_AT_ASC]: 'activation_date_asc',
  [SearchControlSortBy.COMPLETENESS_SCORE_ASC]: 'completeness_asc',
  [SearchControlSortBy.COMPLETENESS_SCORE_DESC]: 'completeness_desc',
  [SearchControlSortBy.PERFORMANCE_ASC]: 'performance_asc',
  [SearchControlSortBy.PERFORMANCE_DESC]: 'performance_desc',
};

export const mappedTrackingStatuses: Record<InsertionStatus, string> = {
  [InsertionStatus.Draft]: 'draft',
  [InsertionStatus.PendingApproval]: 'published',
  [InsertionStatus.PendingRepublishApproval]: 'published',
  [InsertionStatus.Approved]: 'published',
  [InsertionStatus.Published]: 'published',
  [InsertionStatus.Archived]: 'archived',
  [InsertionStatus.Archiving]: 'archived',

  // Deprecated statuses
  [InsertionStatus.CONTACT_REQUESTED]: 'published',
  [InsertionStatus.Rejected]: 'published',
  [InsertionStatus.LegacyMigrated]: 'published',
  [InsertionStatus.Deleted]: 'archived',
  [InsertionStatus.Republishing]: 'published',
};

export const mappedTrackingSources: Record<InsertionSource, string> = {
  [InsertionSource.MANUAL]: 'smg_business_manager',
  [InsertionSource.CRM]: 'external_import',
};

export const mappedTrackingCompleteness: Record<CompletenessLevel, string> = {
  [CompletenessLevel.VERY_HIGH]: 'completeness_very_high',
  [CompletenessLevel.HIGH]: 'completeness_high',
  [CompletenessLevel.MEDIUM]: 'completeness_medium',
  [CompletenessLevel.LOW]: 'completeness_low',
};

export interface SearchControlData {
  [key: string]: SearchControlTypes;
  page?: number;
  sortBy?: SearchControlSortBy;
  feature?: string;
}

export interface SearchPaginationData {
  page?: number;
  pageSize?: number;
  totalPages?: number;
  totalItems?: number;
}

export const mappedBusinessStatuses: Record<string, InsertionStatus[]> = {
  DRAFT: [InsertionStatus.Draft],
  PUBLISHED: [
    InsertionStatus.PendingApproval,
    InsertionStatus.PendingRepublishApproval,
    InsertionStatus.Approved,
    InsertionStatus.Published,
    InsertionStatus.Republishing,
  ],
  ARCHIVED: [InsertionStatus.Archived, InsertionStatus.Archiving],
};

export const searchFilterDefaults = {
  searchQuery: [],
  status: [],
  offerType: [],
  category: [],
  rankType: [],
  source: [],
  completeness: [],
  performance: [],
};

export const searchControlDefaults = {
  page: 1,
  sortBy: SearchControlSortBy.LAST_UPDATE_DESC,
};

export const searchPaginationDefaults = {
  page: searchControlDefaults.page,
  pageSize: 20,
  totalPages: 1,
  totalItems: 0,
};

export const resetRouteQueryPageNumber = (routeQuery: LocationQuery): LocationQuery => {
  return { ...routeQuery, ...{ page: searchControlDefaults.page.toString() } };
};
